'use client';

// Drop-in replacement for the old @trpc/react-query `trpcReact` proxy.
// Exposes the same `trpcReact.<group>.<procedure>.useQuery / useMutation /
// useInfiniteQuery` shape, so existing call sites keep working with the
// `import { trpcReact } from '@web/utils/trpc/trpcReact'` line untouched.
//
// Under the hood: every procedure defined in `procedures.ts` becomes a
// react-query hook keyed by `[group, name, input]`.

import { FC, PropsWithChildren, useMemo } from 'react';

import {
  useInfiniteQuery as rqUseInfiniteQuery,
  useMutation as rqUseMutation,
  useQuery as rqUseQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { proceduresClient, type ProceduresClient } from './procedures';

// Pass-through provider for backwards compatibility with the old layout.
// The real QueryClientProvider lives in `@web/utils/react-query/ReactQueryProvider`,
// which wraps everything below us — these hooks resolve against that client.
export const TrpcProvider: FC<PropsWithChildren> = ({ children }) => (
  <>{children}</>
);

// Generic helpers ----------------------------------------------------------

type AnyProc = (...args: any[]) => Promise<any>;
type ProcInput<P> = P extends (input: infer I) => Promise<any>
  ? I
  : P extends () => Promise<any>
    ? void | undefined
    : never;
type ProcOutput<P> = P extends (...args: any[]) => Promise<infer O> ? O : never;

interface QueryWrapper<P extends AnyProc> {
  useQuery: (
    input?: ProcInput<P>,
    opts?: Omit<
      UseQueryOptions<ProcOutput<P>>,
      'queryKey' | 'queryFn' | 'initialData'
    > & {
      initialData?: ProcOutput<P>;
    },
  ) => ReturnType<typeof rqUseQuery<ProcOutput<P>>>;
  useInfiniteQuery: (
    input: ProcInput<P>,
    opts: Omit<
      UseInfiniteQueryOptions<
        ProcOutput<P>,
        Error,
        InfiniteData<ProcOutput<P>>,
        ProcOutput<P>,
        readonly unknown[],
        number
      >,
      'queryKey' | 'queryFn' | 'initialPageParam'
    > & {
      initialCursor?: number;
      initialPageParam?: number;
    },
  ) => ReturnType<
    typeof rqUseInfiniteQuery<
      ProcOutput<P>,
      Error,
      InfiniteData<ProcOutput<P>>,
      readonly unknown[],
      number
    >
  >;
}

// For procs that take no input, ProcInput resolves to `void | undefined`,
// and react-query's mutate() requires its variables arg even when typed
// `void`. Coerce to optional/no-arg to keep call sites clean.
type MutationVar<P extends AnyProc> =
  ProcInput<P> extends void | undefined ? void : ProcInput<P>;

interface MutationWrapper<P extends AnyProc> {
  useMutation: (
    opts?: UseMutationOptions<ProcOutput<P>, Error, MutationVar<P>>,
  ) => ReturnType<typeof rqUseMutation<ProcOutput<P>, Error, MutationVar<P>>>;
}

function wrapQueryProc<P extends AnyProc>(
  group: string,
  name: string,
  proc: P,
): QueryWrapper<P> & MutationWrapper<P> {
  return {
    useQuery: (input?, opts?) => {
      const queryKey = [group, name, input] as const;
      return rqUseQuery({
        queryKey,
        queryFn: () => (proc as AnyProc)(input),
        ...(opts ?? {}),
      }) as any;
    },
    useInfiniteQuery: (input, opts) => {
      const queryKey = [group, name, input] as const;
      const initialCursor = opts?.initialCursor ?? 1;
      return rqUseInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) =>
          (proc as AnyProc)({ ...(input as object), cursor: pageParam }),
        initialPageParam: initialCursor,
        ...(opts as any),
      }) as any;
    },
    useMutation: (opts?) =>
      rqUseMutation({
        mutationFn: (input: any) => (proc as AnyProc)(input),
        ...(opts ?? {}),
      }) as any,
  };
}

// Build the proxy: `trpcReact.<group>.<proc>.useQuery(...)`.
type TrpcReactShape = {
  [G in keyof ProceduresClient]: {
    [P in keyof ProceduresClient[G]]: ProceduresClient[G][P] extends AnyProc
      ? QueryWrapper<ProceduresClient[G][P]> &
          MutationWrapper<ProceduresClient[G][P]>
      : ProceduresClient[G][P] extends Record<string, AnyProc>
        ? {
            [SP in keyof ProceduresClient[G][P]]: ProceduresClient[G][P][SP] extends AnyProc
              ? QueryWrapper<ProceduresClient[G][P][SP]> &
                  MutationWrapper<ProceduresClient[G][P][SP]>
              : never;
          }
        : never;
  };
};

function build(): TrpcReactShape {
  const out: any = {};
  for (const [group, groupProcs] of Object.entries(proceduresClient)) {
    out[group] = {};
    for (const [name, proc] of Object.entries(groupProcs)) {
      if (typeof proc === 'function') {
        out[group][name] = wrapQueryProc(group, name, proc as AnyProc);
      } else if (proc && typeof proc === 'object') {
        // nested namespace, e.g. user.twitter
        out[group][name] = {};
        for (const [subName, subProc] of Object.entries(proc)) {
          out[group][name][subName] = wrapQueryProc(
            `${group}.${name}`,
            subName,
            subProc as AnyProc,
          );
        }
      }
    }
  }
  return out as TrpcReactShape;
}

export const trpcReact = build() as TrpcReactShape & {
  useUtils: () => UtilsProxy;
};

// `useUtils()` parity: returns a proxy that lets call sites do
// `trpcReact.useUtils().user.invalidate()` etc. Each leaf maps to a
// react-query queryKey invalidation against the shared queryClient.

interface UtilsLeaf {
  invalidate: () => Promise<void>;
}

type UtilsProxy = {
  [G in keyof ProceduresClient]: UtilsLeaf & {
    [P in keyof ProceduresClient[G]]: UtilsLeaf;
  };
};

(trpcReact as any).useUtils = function useUtils(): UtilsProxy {
  const qc = useQueryClient();
  return useMemo(() => {
    const out: any = {};
    for (const group of Object.keys(proceduresClient)) {
      const groupNode: any = {
        invalidate: () => qc.invalidateQueries({ queryKey: [group] }),
      };
      for (const proc of Object.keys((proceduresClient as any)[group] ?? {})) {
        groupNode[proc] = {
          invalidate: () => qc.invalidateQueries({ queryKey: [group, proc] }),
        };
      }
      out[group] = groupNode;
    }
    return out;
  }, [qc]);
};
